import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import AdvancedSearch from '../components/AdvancedSearch';
import {
  Shield, ArrowLeft, Building2, Car, Home, Briefcase, Users, FileCheck, Plane,
  UserCheck, Lock, Building, ArrowRight, Loader2, Scale, Heart, Lightbulb,
  UserCog, Calculator, Tractor, ShoppingBag, TreePine, Mountain, Gavel,
  AlertTriangle, Globe, Laptop, Trophy, HeartPulse, Key, Package, Receipt,
  Leaf, Droplets, Wheat, ShieldCheck, Recycle, Bird, Gem, Pickaxe, Handshake,
  FileText, ArrowUpCircle, Ban, Ship, CreditCard, Cloud, Database, Server,
  Code, ShoppingCart, Cookie, Award, Camera, Stethoscope, Video, Folder,
  HardHat, ClipboardCheck, List, AlertCircle, LogOut, Search, Filter,
  Clock, CheckCircle, XCircle, ChevronDown, Grid3X3, LayoutList, Sparkles,
  SlidersHorizontal
} from 'lucide-react';

// Comprehensive icon map for all template icons
const iconMap = {
  Building2, Car, Home, Briefcase, Users, FileCheck, Plane, UserCheck, Lock,
  Building, Scale, Heart, Lightbulb, UserCog, Calculator, Tractor, ShoppingBag,
  TreePine, Mountain, Gavel, AlertTriangle, Globe, Laptop, Trophy, HeartPulse,
  Key, Package, Receipt, Leaf, Droplets, Wheat, ShieldCheck, Recycle, Bird,
  Gem, Pickaxe, Handshake, FileText, ArrowUpCircle, Ban, Ship, CreditCard,
  Cloud, Database, Server, Code, ShoppingCart, Cookie, Award, Camera,
  Stethoscope, Video, Folder, HardHat, ClipboardCheck, List, AlertCircle, LogOut,
  Shield, Grid3X3, Sparkles
};

// Category metadata with colors and icons
const categoryMeta = {
  civil: { name: "Derecho Civil", icon: Scale, color: "blue", gradient: "from-blue-500 to-blue-600" },
  comercial: { name: "Derecho Comercial", icon: Building2, color: "emerald", gradient: "from-emerald-500 to-emerald-600" },
  laboral: { name: "Derecho Laboral", icon: Briefcase, color: "amber", gradient: "from-amber-500 to-amber-600" },
  familia: { name: "Derecho de Familia", icon: Heart, color: "rose", gradient: "from-rose-500 to-rose-600" },
  propiedad_intelectual: { name: "Propiedad Intelectual", icon: Lightbulb, color: "purple", gradient: "from-purple-500 to-purple-600" },
  servicios: { name: "Prestación de Servicios", icon: UserCog, color: "cyan", gradient: "from-cyan-500 to-cyan-600" },
  administrativo: { name: "Documentos Notariales", icon: FileCheck, color: "slate", gradient: "from-slate-500 to-slate-600" },
  tributario: { name: "Derecho Tributario", icon: Calculator, color: "orange", gradient: "from-orange-500 to-orange-600" },
  agrario: { name: "Derecho Agrario", icon: Tractor, color: "lime", gradient: "from-lime-500 to-lime-600" },
  consumidor: { name: "Derecho del Consumidor", icon: ShoppingBag, color: "pink", gradient: "from-pink-500 to-pink-600" },
  ambiental: { name: "Derecho Ambiental", icon: TreePine, color: "green", gradient: "from-green-500 to-green-600" },
  minero: { name: "Derecho Minero", icon: Mountain, color: "stone", gradient: "from-stone-500 to-stone-600" },
  procesal: { name: "Derecho Procesal", icon: Gavel, color: "red", gradient: "from-red-500 to-red-600" },
  penal: { name: "Derecho Penal", icon: AlertTriangle, color: "red", gradient: "from-red-600 to-red-700" },
  internacional: { name: "Derecho Internacional", icon: Globe, color: "sky", gradient: "from-sky-500 to-sky-600" },
  tecnologia: { name: "Derecho Tecnológico", icon: Laptop, color: "violet", gradient: "from-violet-500 to-violet-600" },
  deportivo: { name: "Derecho Deportivo", icon: Trophy, color: "yellow", gradient: "from-yellow-500 to-yellow-600" },
  salud: { name: "Derecho de la Salud", icon: HeartPulse, color: "teal", gradient: "from-teal-500 to-teal-600" },
  inmobiliario: { name: "Derecho Inmobiliario", icon: Building, color: "indigo", gradient: "from-indigo-500 to-indigo-600" },
  seguros: { name: "Derecho de Seguros", icon: Shield, color: "blue", gradient: "from-blue-600 to-blue-700" },
  cooperativo: { name: "Derecho Cooperativo", icon: Users, color: "fuchsia", gradient: "from-fuchsia-500 to-fuchsia-600" }
};

const getCategoryColor = (category) => {
  const colors = {
    blue: 'bg-blue-500/10 text-blue-400 border-blue-500/30',
    emerald: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30',
    amber: 'bg-amber-500/10 text-amber-400 border-amber-500/30',
    rose: 'bg-rose-500/10 text-rose-400 border-rose-500/30',
    purple: 'bg-purple-500/10 text-purple-400 border-purple-500/30',
    cyan: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/30',
    slate: 'bg-slate-500/10 text-slate-400 border-slate-500/30',
    orange: 'bg-orange-500/10 text-orange-400 border-orange-500/30',
    lime: 'bg-lime-500/10 text-lime-400 border-lime-500/30',
    pink: 'bg-pink-500/10 text-pink-400 border-pink-500/30',
    green: 'bg-green-500/10 text-green-400 border-green-500/30',
    stone: 'bg-stone-500/10 text-stone-400 border-stone-500/30',
    red: 'bg-red-500/10 text-red-400 border-red-500/30',
    sky: 'bg-sky-500/10 text-sky-400 border-sky-500/30',
    violet: 'bg-violet-500/10 text-violet-400 border-violet-500/30',
    yellow: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/30',
    teal: 'bg-teal-500/10 text-teal-400 border-teal-500/30',
    indigo: 'bg-indigo-500/10 text-indigo-400 border-indigo-500/30',
    fuchsia: 'bg-fuchsia-500/10 text-fuchsia-400 border-fuchsia-500/30'
  };
  const meta = categoryMeta[category];
  return meta ? colors[meta.color] || colors.blue : colors.blue;
};

const TemplatesPage = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [useAdvancedSearch, setUseAdvancedSearch] = useState(false);
  const [advancedFilteredTemplates, setAdvancedFilteredTemplates] = useState(null);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await api.get('/templates');
      // Combine system and custom templates
      const systemTemplates = response.data.system_templates || [];
      const customTemplates = response.data.custom_templates || [];
      const allTemplates = [...systemTemplates, ...customTemplates];
      setTemplates(allTemplates);
    } catch (error) {
      console.error('Error fetching templates:', error);
      toast.error('Error al cargar plantillas');
    } finally {
      setLoading(false);
    }
  };

  const categories = useMemo(() => {
    const cats = [...new Set(templates.map(t => t.category))];
    return cats.sort((a, b) => {
      const nameA = categoryMeta[a]?.name || a;
      const nameB = categoryMeta[b]?.name || b;
      return nameA.localeCompare(nameB);
    });
  }, [templates]);

  const filteredTemplates = useMemo(() => {
    let result = templates;
    
    if (selectedCategory !== 'all') {
      result = result.filter(t => t.category === selectedCategory);
    }
    
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(t => 
        t.name.toLowerCase().includes(query) ||
        t.description.toLowerCase().includes(query) ||
        t.category.toLowerCase().includes(query)
      );
    }
    
    return result;
  }, [templates, selectedCategory, searchQuery]);

  // Use advanced search results if available, otherwise use basic filtered
  const displayTemplates = useMemo(() => {
    if (useAdvancedSearch && advancedFilteredTemplates !== null) {
      return advancedFilteredTemplates;
    }
    return filteredTemplates;
  }, [useAdvancedSearch, advancedFilteredTemplates, filteredTemplates]);

  const groupedTemplates = useMemo(() => {
    if (selectedCategory !== 'all') return null;
    
    const grouped = {};
    displayTemplates.forEach(template => {
      if (!grouped[template.category]) {
        grouped[template.category] = [];
      }
      grouped[template.category].push(template);
    });
    return grouped;
  }, [displayTemplates, selectedCategory]);

  const handleSelectTemplate = (templateId) => {
    // Navigate directly to template fill form (no questions)
    navigate(`/plantilla/${templateId}`);
  };

  const getCategoryDisplayName = (category) => {
    return categoryMeta[category]?.name || category.charAt(0).toUpperCase() + category.slice(1);
  };

  const TemplateCard = ({ template, index }) => {
    const IconComponent = iconMap[template.icon] || FileCheck;
    const colorClass = getCategoryColor(template.category);
    
    return (
      <button
        onClick={() => handleSelectTemplate(template.id)}
        data-testid={`template-${template.id}`}
        className="bg-card border border-border rounded-xl p-5 text-left card-hover group animate-fade-in w-full"
        style={{ animationDelay: `${(index % 20) * 0.03}s` }}
      >
        <div className="flex items-start justify-between mb-3">
          <div className={`w-11 h-11 rounded-lg flex items-center justify-center ${colorClass} border`}>
            <IconComponent className="w-5 h-5" />
          </div>
          <div className="flex items-center gap-2">
            {template.requires_notary && (
              <span className="text-xs px-2 py-1 rounded bg-amber-500/10 text-amber-400 border border-amber-500/30">
                Notaría
              </span>
            )}
            <ArrowRight className="w-4 h-4 text-slate-600 group-hover:text-blue-400 group-hover:translate-x-1 transition-all" />
          </div>
        </div>
        <h3 className="text-base font-semibold font-['Outfit'] mb-1.5 group-hover:text-blue-400 transition-colors line-clamp-1">
          {template.name}
        </h3>
        <p className="text-sm text-slate-400 mb-3 line-clamp-2">{template.description}</p>
        <div className="flex items-center justify-between">
          <span className={`text-xs px-2 py-1 rounded border ${colorClass}`}>
            {getCategoryDisplayName(template.category)}
          </span>
          <div className="flex items-center gap-1 text-xs text-slate-500">
            <Clock className="w-3 h-3" />
            {template.estimated_time}
          </div>
        </div>
      </button>
    );
  };

  return (
    <div className="min-h-screen mesh-gradient">
      {/* Header */}
      <header className="glass border-b border-border sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/dashboard" className="flex items-center space-x-3 text-slate-400 hover:text-white transition-colors">
              <ArrowLeft className="w-5 h-5" />
              <span className="hidden sm:inline">Dashboard</span>
            </Link>
            <div className="flex items-center space-x-3">
              <Shield className="w-6 h-6 text-blue-500" />
              <span className="font-semibold font-['Outfit']">Biblioteca Legal</span>
            </div>
            <div className="text-sm text-slate-400">
              {templates.length} plantillas
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Hero Section */}
        <div className="text-center mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold font-['Outfit'] mb-3">
            Biblioteca Completa de Contratos
          </h1>
          <p className="text-slate-400 max-w-2xl mx-auto text-sm sm:text-base">
            Más de {templates.length} plantillas legales cubriendo todas las áreas del derecho chileno.
            Selecciona una para comenzar con asistencia IA.
          </p>
        </div>

        {/* Smart Contract Creation Banner */}
        <Link
          to="/crear-contrato"
          className="block mb-8 bg-gradient-to-r from-blue-600/20 to-purple-600/20 border border-blue-500/30 rounded-xl p-6 hover:from-blue-600/30 hover:to-purple-600/30 transition-all group"
          data-testid="smart-contract-creation-link"
        >
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
                <Sparkles className="w-7 h-7 text-white" />
              </div>
              <div className="text-center sm:text-left">
                <h3 className="text-lg font-bold text-white flex items-center gap-2">
                  Creación Inteligente de Contratos
                  <span className="text-xs px-2 py-0.5 bg-blue-500 rounded-full">NUEVO</span>
                </h3>
                <p className="text-slate-400 text-sm">
                  Selecciona tu rol y recibe cláusulas protectoras personalizadas con IA
                </p>
              </div>
            </div>
            <ArrowRight className="w-6 h-6 text-blue-400 group-hover:translate-x-2 transition-transform" />
          </div>
        </Link>

        {/* Search and Filters */}
        <div className="mb-6">
          {/* Toggle Advanced Search */}
          <div className="flex justify-end mb-3">
            <button
              onClick={() => setUseAdvancedSearch(!useAdvancedSearch)}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-colors ${
                useAdvancedSearch 
                  ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30' 
                  : 'bg-slate-800 text-slate-400 border border-slate-700 hover:bg-slate-700'
              }`}
              data-testid="toggle-advanced-search"
            >
              <SlidersHorizontal className="w-4 h-4" />
              <span>{useAdvancedSearch ? 'Búsqueda simple' : 'Búsqueda avanzada'}</span>
            </button>
          </div>

          {useAdvancedSearch ? (
            <AdvancedSearch 
              templates={templates} 
              onFilteredTemplates={setAdvancedFilteredTemplates}
              initialCategory={selectedCategory}
            />
          ) : (
            <div className="flex flex-col sm:flex-row gap-4">
              {/* Search */}
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="text"
                  placeholder="Buscar contratos..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  data-testid="search-templates"
                  className="w-full pl-10 pr-4 py-2.5 bg-slate-800/50 border border-border rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-blue-500 transition-colors"
                />
              </div>

              {/* Category Dropdown */}
              <div className="relative">
                <button
                  onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
                  data-testid="category-dropdown"
                  className="flex items-center gap-2 px-4 py-2.5 bg-slate-800/50 border border-border rounded-lg text-white hover:border-blue-500 transition-colors min-w-[200px] justify-between"
                >
                  <div className="flex items-center gap-2">
                    <Filter className="w-4 h-4 text-slate-400" />
                    <span className="text-sm">
                      {selectedCategory === 'all' ? 'Todas las Categorías' : getCategoryDisplayName(selectedCategory)}
                    </span>
                  </div>
                  <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${showCategoryDropdown ? 'rotate-180' : ''}`} />
                </button>

                {showCategoryDropdown && (
                  <div className="absolute top-full mt-2 w-72 bg-slate-800 border border-border rounded-lg shadow-xl z-50 max-h-96 overflow-y-auto">
                    <button
                      onClick={() => { setSelectedCategory('all'); setShowCategoryDropdown(false); }}
                      className={`w-full px-4 py-3 text-left text-sm hover:bg-slate-700 transition-colors flex items-center gap-3 ${selectedCategory === 'all' ? 'bg-blue-600/20 text-blue-400' : 'text-white'}`}
                    >
                      <Grid3X3 className="w-4 h-4" />
                      Todas las Categorías
                      <span className="ml-auto text-xs text-slate-400">{templates.length}</span>
                    </button>
                    <div className="border-t border-border" />
                    {categories.map((category) => {
                      const meta = categoryMeta[category];
                      const CategoryIcon = meta?.icon || FileCheck;
                      const count = templates.filter(t => t.category === category).length;
                      return (
                        <button
                          key={category}
                          onClick={() => { setSelectedCategory(category); setShowCategoryDropdown(false); }}
                      data-testid={`category-option-${category}`}
                      className={`w-full px-4 py-3 text-left text-sm hover:bg-slate-700 transition-colors flex items-center gap-3 ${selectedCategory === category ? 'bg-blue-600/20 text-blue-400' : 'text-white'}`}
                    >
                      <CategoryIcon className="w-4 h-4" />
                      {getCategoryDisplayName(category)}
                      <span className="ml-auto text-xs text-slate-400">{count}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* View Toggle */}
          <div className="flex items-center gap-1 bg-slate-800/50 border border-border rounded-lg p-1">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded ${viewMode === 'grid' ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-white'}`}
            >
              <Grid3X3 className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded ${viewMode === 'list' ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-white'}`}
            >
              <LayoutList className="w-4 h-4" />
            </button>
          </div>
            </div>
          )}
        </div>

        {/* Category Pills (Quick Filter) */}
        <div className="flex flex-wrap gap-2 mb-6 pb-4 border-b border-border">
          <button
            onClick={() => setSelectedCategory('all')}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
              selectedCategory === 'all'
                ? 'bg-blue-600 text-white'
                : 'bg-slate-800/50 text-slate-400 hover:text-white hover:bg-slate-700'
            }`}
          >
            Todas ({templates.length})
          </button>
          {categories.slice(0, 8).map((category) => {
            const count = templates.filter(t => t.category === category).length;
            return (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                data-testid={`quick-category-${category}`}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                  selectedCategory === category
                    ? 'bg-blue-600 text-white'
                    : 'bg-slate-800/50 text-slate-400 hover:text-white hover:bg-slate-700'
                }`}
              >
                {getCategoryDisplayName(category)} ({count})
              </button>
            );
          })}
          {categories.length > 8 && (
            <button
              onClick={() => setShowCategoryDropdown(true)}
              className="px-3 py-1.5 rounded-full text-xs font-medium bg-slate-800/50 text-slate-400 hover:text-white hover:bg-slate-700"
            >
              +{categories.length - 8} más...
            </button>
          )}
        </div>

        {loading ? (
          <div className="text-center py-20">
            <Loader2 className="w-10 h-10 animate-spin mx-auto text-blue-500 mb-4" />
            <p className="text-slate-400">Cargando biblioteca legal...</p>
          </div>
        ) : displayTemplates.length === 0 ? (
          <div className="text-center py-20">
            <Search className="w-12 h-12 mx-auto text-slate-600 mb-4" />
            <h3 className="text-lg font-semibold mb-2">No se encontraron plantillas</h3>
            <p className="text-slate-400 mb-4">Intenta con otros términos de búsqueda o cambia la categoría</p>
            <button
              onClick={() => { setSearchQuery(''); setSelectedCategory('all'); setAdvancedFilteredTemplates(null); }}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Limpiar filtros
            </button>
          </div>
        ) : selectedCategory === 'all' && !searchQuery && groupedTemplates ? (
          // Grouped View
          <div className="space-y-10">
            {Object.entries(groupedTemplates).map(([category, categoryTemplates]) => {
              const meta = categoryMeta[category];
              const CategoryIcon = meta?.icon || FileCheck;
              return (
                <section key={category}>
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center bg-gradient-to-br ${meta?.gradient || 'from-blue-500 to-blue-600'}`}>
                      <CategoryIcon className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold font-['Outfit']">{getCategoryDisplayName(category)}</h2>
                      <p className="text-sm text-slate-400">{categoryTemplates.length} plantillas</p>
                    </div>
                    <button
                      onClick={() => setSelectedCategory(category)}
                      className="ml-auto text-sm text-blue-400 hover:text-blue-300 flex items-center gap-1"
                    >
                      Ver todas <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                  <div className={`grid gap-4 ${viewMode === 'grid' ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4' : 'grid-cols-1'}`}>
                    {categoryTemplates.slice(0, viewMode === 'grid' ? 4 : 3).map((template, index) => (
                      <TemplateCard key={template.id} template={template} index={index} />
                    ))}
                  </div>
                </section>
              );
            })}
          </div>
        ) : (
          // Flat View
          <div className={`grid gap-4 ${viewMode === 'grid' ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4' : 'grid-cols-1'}`}>
            {filteredTemplates.map((template, index) => (
              <TemplateCard key={template.id} template={template} index={index} />
            ))}
          </div>
        )}

        {/* Stats Footer */}
        <div className="mt-12 pt-8 border-t border-border">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div>
              <div className="text-3xl font-bold text-blue-400">{templates.length}</div>
              <div className="text-sm text-slate-400">Plantillas Totales</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-emerald-400">{categories.length}</div>
              <div className="text-sm text-slate-400">Áreas del Derecho</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-purple-400">
                {templates.filter(t => t.requires_notary).length}
              </div>
              <div className="text-sm text-slate-400">Requieren Notaría</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-amber-400">
                {templates.filter(t => t.complexity === 'simple').length}
              </div>
              <div className="text-sm text-slate-400">Trámites Simples</div>
            </div>
          </div>
        </div>
      </main>

      {/* Click outside to close dropdown */}
      {showCategoryDropdown && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => setShowCategoryDropdown(false)}
        />
      )}
    </div>
  );
};

export default TemplatesPage;
